import { SuperAdminUserOverviewModal } from "./SuperAdminUserOverviewModal"
import Button from "@ui/button/Button"
import { useCallback, useEffect, useRef, useState } from "react"
import Config from "src/Config"
import { superAdminFetch as fetch } from "src/services/superAdminFetch"
import { T } from "../adminTheme"
import { accessLocks, isBlocked } from "./userAccessLocks"
import { ActionMenu, Avatar, FilterBar, KycState, linkRow, Muted, OpenCell, Page, Pager, Panel, Row, SearchField, sentence, State, StatusBadge, Table } from "./adminUi"

export interface ISystemUserDirectoryRow {
	uid: string
	name: string
	email: string
	status: string
	company_uid: string
	company_name?: string
	company_type?: string
	company_role?: string
	company_status?: string
	kyc_status?: string
	/** Our KYC applies to this user (private account or company admin). */
	kyc_required?: boolean
	kyc_state?: string
	email_confirmed_at?: string | null
	/** Not sent by system/user/list yet; once it is, corporate rows get the right e-mail lock too. */
	registration_source?: string
	team_uid?: string | null
	team_name?: string | null
	team_status?: string | null
	phone?: string | null
	cards_count?: number
	active_cards_count?: number
	cardholders_count?: number
	approved_cardholders_count?: number
	created_at: string
	team_balance?: number
	team_balances?: Array<{
		issuer: number
		currency: string
		team_balance: number
	}>
	__actions?: null
}

interface DirectoryResponse<T> {
	total: number
	limit: number
	offset: number
	rows: T[]
}

export interface ISystemOverviewBalance {
	issuer: number
	issuer_label?: string
	provider_code?: string
	is_default?: boolean
	currency: string
	amount: number
}

export interface ISystemUserOverview {
	user?: Record<string, unknown>
	company?: Record<string, unknown> | null
	issuers?: Array<Record<string, unknown>>
	teams?: Array<Record<string, unknown>>
	balances?: {
		buyer?: ISystemOverviewBalance[]
		company?: ISystemOverviewBalance[]
	}
	kyc?: {
		our_status?: string
		verifications?: Array<Record<string, unknown>>
		attempts?: Array<Record<string, unknown>>
		issuers?: Array<Record<string, unknown>>
	}
	cardholders?: Array<Record<string, unknown>>
	cards?: {
		total?: number
		by_status?: Record<string, number>
		balance_cents?: number
		balance_source?: string
		list?: Array<Record<string, unknown>>
	}
	block_operations?: Array<Record<string, unknown>>
	origin?: {
		created_at?: string
		email_confirmed_at?: string
		invited_by?: Record<string, unknown> | null
		invites?: Array<Record<string, unknown>>
	}
	sessions?: {
		active?: number
		expired?: number
		last_login_at?: string | null
		expires_at?: string | null
	}
	events?: { total?: number; rows?: Array<Record<string, unknown>> }
	transactions?: {
		supported?: boolean
		reason?: string | null
		rows?: Array<Record<string, unknown>>
		totals?: Record<string, unknown>
		balances?: Record<string, unknown>
	} | null
	flow?: {
		login_enabled?: boolean
		email_confirmed?: boolean
		our_kyc_status?: string
		approved_holders?: number
		approved_issuers?: string[]
		can_issue_cards?: boolean
		blocking?: string[]
		next_action?: string
	}
}

interface Props {
	token: string
	companies: Array<{ uid: string; name: string }>
	initialCompanyUid?: string
	onRowsLoaded?: (rows: ISystemUserDirectoryRow[]) => void
	onAdjust: (direction: "in" | "out", user: ISystemUserDirectoryRow) => void
	onShowAllEvents?: (uid: string) => void
	onOpenCompany?: (uid: string) => void
}

const PAGE_SIZE = 50
const ALL: [string, string] = ["all", "All"]
const ROLES: Record<string, string> = { admin: "Admin", teamlead: "Team lead", buyer: "Buyer" }
const EMPTY_USER_FILTERS = {
	search: "",
	company_uid: "all",
	company_type: "all",
	roles: "all",
	status: "all",
	company_status: "all",
	kyc_status: "all",
	team_uid: ""
}

const normalizeDirectory = <T,>(data: T[] | DirectoryResponse<T> | null | undefined): DirectoryResponse<T> => {
	if (Array.isArray(data)) return { total: data.length, limit: data.length, offset: 0, rows: data }
	return {
		total: Number(data?.total ?? 0),
		limit: Number(data?.limit ?? PAGE_SIZE),
		offset: Number(data?.offset ?? 0),
		rows: Array.isArray(data?.rows) ? data.rows : []
	}
}

/** Only the locks that hold this user. Without registration_source in the row, a corporate user shows no e-mail lock. */
const AccessBadges = ({ row }: { row: ISystemUserDirectoryRow }) => {
	const locks = accessLocks({ companyType: row.company_type, companyRole: row.company_role, registrationSource: row.registration_source })
	return <div style={{ display: "grid", gap: 3, justifyItems: "start" }}>
		{locks.email && <Row style={{ gap: 6, flexWrap: "nowrap" }}><Muted>Email</Muted><StatusBadge value={row.email_confirmed_at === null ? "unconfirmed" : row.email_confirmed_at ? "confirmed" : "unknown"} /></Row>}
		{(row.kyc_required ?? locks.kyc) && <Row style={{ gap: 6, flexWrap: "nowrap" }}><Muted>KYC</Muted><StatusBadge value={row.kyc_status || "unknown"} /></Row>}
		{isBlocked(row.status, row.company_status) && <StatusBadge value='blocked' />}
		{!locks.email && !(row.kyc_required ?? locks.kyc) && !isBlocked(row.status, row.company_status) && <Muted>—</Muted>}
	</div>
}

export const SuperAdminUserDirectory = ({ token, companies, initialCompanyUid, onRowsLoaded, onAdjust, onShowAllEvents, onOpenCompany }: Props) => {
	const [filters, setFilters] = useState({ ...EMPTY_USER_FILTERS, company_uid: initialCompanyUid || "all" })
	const [page, setPage] = useState(0)
	const [result, setResult] = useState<DirectoryResponse<ISystemUserDirectoryRow>>({
		total: 0,
		limit: PAGE_SIZE,
		offset: 0,
		rows: []
	})
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState("")
	const [overviewUid, setOverviewUid] = useState<string | null>(null)
	const requestSequence = useRef(0)
	const knownTeams = useRef(new Map<string, string>())

	const load = useCallback(async () => {
		const requestId = ++requestSequence.current
		setLoading(true)
		setError("")
		try {
			const body = Object.fromEntries(Object.entries(filters).filter(([, item]) => item !== "" && item !== "all"))
			const response = await fetch(Config.scheme.URL_API + "system/user/list", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify({
					...body,
					limit: PAGE_SIZE,
					offset: page * PAGE_SIZE,
					with_meta: true
				})
			})
			const payload = await response.json()
			if (!payload.data) throw new Error(payload.error?.message || "Users could not be loaded")
			const normalized = normalizeDirectory<ISystemUserDirectoryRow>(payload.data)
			normalized.rows = normalized.rows.map((row) => ({
				...row,
				__actions: null
			}))
			if (requestId !== requestSequence.current) return
			normalized.rows.forEach((row) => { if (row.team_uid) knownTeams.current.set(row.team_uid, [row.team_name || row.team_uid, row.company_name].filter(Boolean).join(" · ")) })
			setResult(normalized)
			onRowsLoaded?.(normalized.rows)
		} catch (cause) {
			if (requestId !== requestSequence.current) return
			setResult({ total: 0, limit: PAGE_SIZE, offset: 0, rows: [] })
			setError(cause instanceof Error ? cause.message : "Users could not be loaded")
		} finally {
			if (requestId === requestSequence.current) setLoading(false)
		}
	}, [filters, onRowsLoaded, page, token])

	useEffect(() => {
		const timer = window.setTimeout(() => void load(), filters.search ? 300 : 0)
		return () => {
			window.clearTimeout(timer)
			requestSequence.current += 1
		}
	}, [load, filters.search])

	const setFilter = (key: keyof typeof filters, next: string) => {
		setPage(0)
		setFilters((current) => ({ ...current, [key]: next }))
	}
	const from = result.total ? result.offset + 1 : 0
	const to = Math.min(result.offset + result.rows.length, result.total)
	const hasNext = result.offset + result.rows.length < result.total
	const choose = (key: keyof typeof filters, label: string, options: Array<[string, string]>) =>
		<label>{label}<select name={key} value={filters[key]} onChange={(event) => setFilter(key, event.target.value)}>{options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select></label>

	return (
		<Page>
			<Panel title='Find users' hint='Search is live. Narrow the list with the filters below.' actions={<>
				<Button variant="secondarySmall" onClick={() => { setPage(0); setFilters({ ...EMPTY_USER_FILTERS }) }}>Reset</Button>
				<Button variant="secondarySmall" disabled={loading} onClick={() => void load()}>{loading ? "Loading…" : "Refresh"}</Button>
			</>}>
				<div style={{ display: "grid", gap: 14 }}>
					<SearchField label='Search' placeholder='Name, email, UID, phone, cardholder or company' value={filters.search} onChange={(value) => setFilter("search", value)} />
					<FilterBar>
						{choose("company_uid", "Company", [["all", "All companies"], ...companies.map((company): [string, string] => [company.uid, company.name])])}
						{choose("company_type", "Account type", [["all", "All accounts"], ["private", "Private"], ["company", "Company"]])}
						{choose("roles", "Role", [ALL, ["admin", "Admin"], ["teamlead", "Team lead"], ["buyer", "Buyer"], ["buyer,teamlead", "Buyer + team lead"]])}
						{choose("kyc_status", "KYC", [ALL, ...["pending", "under_review", "approved", "declined", "expired"].map((item): [string, string] => [item, sentence(item)])])}
						{choose("status", "User status", [ALL, ["active", "Active"], ["disabled", "Disabled"], ["deleted", "Deleted"]])}
						{choose("company_status", "Company access", [ALL, ["active", "Active"], ["disabled", "Disabled"]])}
						<label>Team<select name="team_uid" value={filters.team_uid} onChange={(event) => setFilter("team_uid", event.target.value)}>
							<option value="">Any team</option>
							{/* No team list endpoint: offer every team seen in loaded users, plus the current value. */}
							{[...knownTeams.current].map(([uid, label]) => <option key={uid} value={uid}>{label}</option>)}
							{filters.team_uid && !knownTeams.current.has(filters.team_uid) && <option value={filters.team_uid}>{filters.team_uid}</option>}
						</select></label>
					</FilterBar>
				</div>
			</Panel>
			<Panel title='Users' hint='Click a user to open the full profile.'>
				<State loading={loading && result.rows.length === 0} error={error || null} empty={!loading && !error && result.rows.length === 0} emptyText='No users match these filters.' />
				{result.rows.length > 0 && <div style={{ opacity: loading ? 0.6 : 1, transition: `opacity .16s ${T.ease}` }}><Table>
					<thead><tr><th>Person</th><th>Company and role</th><th>Contact</th><th>Access</th><th>KYC</th><th>Active cards</th><th>Quick actions</th><th /></tr></thead>
					<tbody>{result.rows.map((row) => <tr key={row.uid} {...linkRow(() => setOverviewUid(row.uid))}>
						<td>
							<Row style={{ gap: 10, flexWrap: "nowrap" }}>
								<Avatar name={row.name || row.email} size={30} />
								<div style={{ minWidth: 0 }}>
									<Row style={{ gap: 8 }}><span style={{ fontWeight: 600 }}>{row.name || "Unnamed user"}</span><StatusBadge value={row.status} /></Row>
									<small><code>{row.uid}</code></small>
									{row.company_type === "private" && <small style={{ color: T.accentText, fontWeight: 550 }}>Private account</small>}
								</div>
							</Row>
						</td>
						<td style={{ minWidth: 150 }}>
							{row.company_name || companies.find((company) => company.uid === row.company_uid)?.name || row.company_uid}
							<small>{ROLES[row.company_role ?? ""] ?? row.company_role ?? "No role"} · {row.team_name || "No team"}</small>
							{row.company_status && row.company_status !== "active" && <small style={{ color: T.danger }}>Company access: {row.company_status}</small>}
						</td>
						<td><span style={{ overflowWrap: "break-word" }}>{row.email}</span><small>{row.phone || "No phone"}</small></td>
						<td><AccessBadges row={row} /></td>
						<td><KycState state={row.kyc_state} status={row.kyc_status} /></td>
						<td><span style={{ fontWeight: 600 }}>{row.active_cards_count ?? 0}</span><Muted> of {row.cards_count ?? 0}</Muted></td>
						<td onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
							<ActionMenu label='Actions' items={[
								...(onOpenCompany ? [["Open client", () => onOpenCompany(row.company_uid)] as [string, () => void]] : []),
								["Add money to this balance", () => onAdjust("in", row)],
								["Take money off this balance", () => onAdjust("out", row)]
							]} />
						</td>
						<OpenCell />
					</tr>)}</tbody>
				</Table></div>}
				<Pager total={result.total} from={from} to={to} page={page} pages={Math.max(1, Math.ceil(result.total / PAGE_SIZE))} onPage={(next) => { if (!loading && (next < page || hasNext)) setPage(next) }} />
			</Panel>
			{overviewUid && <SuperAdminUserOverviewModal token={token} userUid={overviewUid} onClose={() => setOverviewUid(null)} onShowAllEvents={onShowAllEvents} />}
		</Page>
	)
}
